/* MyLawyerCard — بطاقة المحامي صاحب الحساب أعلى صفحة دليل المحامين.
   تُظهِر تَنبيهاً عند نَقص المعلومات، مع زرّ "إكمال التَّسجيل" يَنقُل إلى صفحة الحساب. */
import { Link } from "react-router-dom";
import {
  WarningCircle, CheckCircle, MapPin, Crown, PencilSimple, Eye, EyeSlash,
  Share, WhatsappLogo, LinkSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { avatarUrl } from "../../lib/api";
import { computeCompleteness } from "../../lib/profileCompleteness";
import { buildWhatsappShareLink, copyShareUrl, nativeShare } from "../../lib/lawyerShare";
import { gradeLabel, isVerified } from "../../lib/lawyerVerification";
import VerifiedBadge, { GradeChip, HotChip } from "./VerifiedBadge";
import ViewStatsBadge from "./ViewStatsBadge";
import ProfileViewsPanel from "./ProfileViewsPanel";

function ShareButtons({ user }) {
  const payload = {
    name: user?.name,
    specialty: user?.specialty,
    city: user?.city || user?.governorate,
    slug: user?.slug,
  };
  const onCopy = async () => {
    try {
      const url = await copyShareUrl(user.slug);
      toast.success("تَمّ نَسخ رابِط بِطاقَتك", { description: url });
    } catch {
      toast.error("تَعَذَّر النَّسخ");
    }
  };
  const onNative = async () => {
    const ok = await nativeShare(payload);
    if (!ok) await onCopy();
  };
  return (
    <div className="mt-3 grid grid-cols-3 gap-2" data-testid="share-row">
      <button
        type="button" onClick={onNative}
        data-testid="share-native-btn"
        className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-[#0A192F] text-white text-[12px] font-bold active:scale-95 transition"
      >
        <Share size={13} weight="bold" /> مُشارَكة
      </button>
      <a
        href={buildWhatsappShareLink(payload)}
        target="_blank" rel="noopener noreferrer"
        data-testid="share-whatsapp-btn"
        className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-[#25D366] text-white text-[12px] font-bold active:scale-95 transition shadow-[0_3px_10px_rgba(37,211,102,0.30)]"
      >
        <WhatsappLogo size={13} weight="fill" /> واتساب
      </a>
      <button
        type="button" onClick={onCopy}
        data-testid="share-copy-btn"
        className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-white border border-[#D4AF37] text-[#0A192F] text-[12px] font-bold active:scale-95 transition"
      >
        <LinkSimple size={13} weight="bold" /> نَسخ الرابِط
      </button>
    </div>
  );
}

export default function MyLawyerCard({ user }) {
  const c = computeCompleteness(user);
  const optedIn = Boolean(user?.leaderboard_opt_in);
  const verified = isVerified(user);
  const grade = gradeLabel(user?.bar_validity_grade);
  const initials = (user?.name || "؟").split(" ").slice(0, 2).map((s) => s[0] || "").join("");

  return (
    <section
      data-testid="my-lawyer-card"
      className="relative bg-white rounded-[24px] p-4 mb-4 shadow-[0_6px_22px_rgba(10,25,47,0.08)] border-2"
      style={{ borderColor: c.ready && optedIn ? "#22C55E" : "#D4AF37" }}
    >
      {/* شريط الحالة */}
      <div className="absolute -top-2.5 right-4 flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10.5px] font-bold"
        style={{ background: c.ready && optedIn ? "#22C55E" : "#D4AF37", color: "#fff" }}
      >
        <Crown size={11} weight="fill" />
        بطاقتي
      </div>

      <div className="flex items-center gap-3.5">
        <div className="w-16 h-16 rounded-full overflow-hidden bg-[#F5F5F5] flex items-center justify-center shrink-0 ring-2 ring-[#D4AF37]/40">
          {user?.avatar_id ? (
            <img src={avatarUrl(user.avatar_id)} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[18px] font-bold text-[#5A6470]">{initials}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[15.5px] font-bold text-[#0A192F] truncate" data-testid="my-card-name">{user?.name}</span>
            {verified && <VerifiedBadge size={15} testid="my-card-verified" />}
            {grade && <GradeChip grade={grade.short} full={grade.full} color={grade.color} />}
            {user?.is_hot && <HotChip />}
            {optedIn && <ViewStatsBadge />}
          </div>
          <div className="text-[12px] text-[#5A6470] mt-0.5 line-clamp-1">
            {user?.specialty || <span className="italic opacity-70">لم يُحدَّد تَخصُّص بعد</span>}
          </div>
          <div className="flex items-center gap-2 mt-1 text-[11px] text-[#5A6470]">
            {user?.governorate && (
              <span className="inline-flex items-center gap-1"><MapPin size={11} weight="fill" />{user.governorate}</span>
            )}
            {user?.district && (
              <span className="inline-flex items-center gap-1 before:content-['•'] before:mx-1">{user.district}</span>
            )}
          </div>
        </div>
        {/* مؤشر الظهور — قابل للنقر، يَفتح صَفحة الإعدادات مُباشَرة */}
        <Link
          to="/account/presence"
          data-testid="visibility-status-link"
          aria-label={optedIn ? "إيقاف الظهور للعملاء" : "تَفعيل الظهور للعملاء"}
          className="flex flex-col items-center gap-1 shrink-0 active:scale-95 transition-transform"
        >
          {optedIn ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10.5px] font-bold border border-emerald-200">
              <Eye size={11} weight="fill" /> ظاهر
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-[10.5px] font-bold border border-gray-200">
              <EyeSlash size={11} weight="fill" /> مَخفيّ
            </span>
          )}
          <span className="text-[9px] text-[#8A8A8A]">تَغيير</span>
        </Link>
      </div>

      {/* شريط تَقدّم اكتمال الملف */}
      <div className="mt-3.5">
        <div className="flex items-center justify-between text-[11px] text-[#5A6470] mb-1">
          <span>اكتمال الملف</span>
          <span className="tabular-nums font-bold text-[#0A192F]">{c.percent}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-[#F0F0F0] overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${c.percent}%`,
              background: c.ready
                ? "linear-gradient(90deg, #22C55E 0%, #16A34A 100%)"
                : "linear-gradient(90deg, #D4AF37 0%, #B8941F 100%)",
            }}
          />
        </div>
      </div>

      {/* تَنبيه إكمال أو نَجاح */}
      {!c.ready ? (
        <div
          className="mt-3 rounded-2xl border border-[#FCD34D] bg-[#FFFBEB] p-3"
          data-testid="completion-alert"
        >
          <div className="flex items-start gap-2">
            <WarningCircle size={16} weight="fill" className="text-[#D97706] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-bold text-[#92400E] mb-1">أَكمل التَّسجيل لِيَظهَر حسابك</div>
              <ul className="text-[11.5px] text-[#7C2D12] leading-relaxed list-disc pr-4 space-y-0.5">
                {c.criticalMissing.slice(0, 3).map((f) => (
                  <li key={f.key}>{f.label} — <span className="opacity-80">{f.hint}</span></li>
                ))}
              </ul>
            </div>
          </div>
          <Link
            to="/account/presence"
            data-testid="complete-profile-btn"
            className="mt-3 inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 rounded-full bg-[#0A192F] text-white text-[12.5px] font-bold active:scale-95 transition"
          >
            <PencilSimple size={13} weight="bold" /> إكمال التَّسجيل الآن
          </Link>
        </div>
      ) : !optedIn ? (
        <div className="mt-3 rounded-2xl border border-[#D4AF37]/40 bg-[#FFFEF7] p-3">
          <div className="text-[12.5px] text-[#0A192F] font-bold mb-1">ملفّك جاهز — لكن مَخفيّ حالياً</div>
          <p className="text-[11.5px] text-[#5A6470] leading-relaxed mb-2">
            فَعِّل خاصيّة الظهور لِيَراك العُملاء في الدليل.
          </p>
          <Link
            to="/account/presence"
            data-testid="enable-public-btn"
            className="inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 rounded-full bg-[#D4AF37] text-[#0A192F] text-[12.5px] font-bold active:scale-95 transition"
          >
            <Eye size={13} weight="bold" /> تَفعيل الظهور
          </Link>
        </div>
      ) : (
        <>
          <div
            className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-3"
            data-testid="profile-ready-banner"
          >
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={16} weight="fill" className="text-emerald-600" />
              <div className="text-[12px] text-emerald-800 leading-snug flex-1">
                ملفّك ظاهر للعملاء
              </div>
            </div>
            {/* أزرار سريعة: تَعديل + مُعاينة. كانت مَفقودة في هذه الحالة فلا
                يَجِد المحامي طَريقاً لِتَغيير الصورة أو إيقاف الظهور. */}
            <div className="grid grid-cols-2 gap-2">
              <Link
                to="/account/presence"
                data-testid="edit-profile-btn"
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#0A192F] text-white text-[12px] font-bold active:scale-95 transition shadow-[0_3px_10px_rgba(10,25,47,0.20)]"
              >
                <PencilSimple size={13} weight="bold" /> تَعديل الملف
              </Link>
              {user?.slug ? (
                <Link
                  to={`/lawyers/${user.slug}`}
                  data-testid="preview-my-profile"
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-white border border-emerald-300 text-emerald-700 text-[12px] font-bold active:scale-95 transition"
                >
                  <Eye size={13} weight="bold" /> مُعاينة
                </Link>
              ) : (
                <span aria-hidden="true" />
              )}
            </div>
          </div>
          {user?.slug && <ShareButtons user={user} />}
          {user?.slug && <ProfileViewsPanel slug={user.slug} />}
        </>
      )}
    </section>
  );
}
